import { create } from 'zustand';
import apiClient from '../lib/api';
import { getUserAvatarUrl } from '../utils/avatar';

interface Employee {
  id: string;
  employeeCode: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  avatar?: string;
  department?: string;
  designation?: string;
  role?: string;
  roleId?: string;
  reportingManagerId?: string;
  status: 'active' | 'on-leave' | 'terminated' | 'resigned';
  employmentType?: string;
  joinDate: string;
  location?: string;
  phone?: string;
  managerName?: string;
  salary?: number;
  currency?: string;
  skills?: any[];
  career?: any[];
  documents?: any[];
}

interface EmployeeState {
  employees: Employee[];
  currentEmployee: Employee | null;
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  stats: {
    total: number;
    byStatus: Record<string, number>;
  } | null;

  fetchEmployees: (filters?: any) => Promise<void>;
  fetchAllEmployees: (filters?: any) => Promise<Employee[]>;
  fetchStats: () => Promise<void>;
  fetchEmployee: (id: string) => Promise<void>;
  createEmployee: (data: Partial<Employee> & { roleId?: string; role?: string }) => Promise<void>;
  updateEmployee: (id: string, data: Partial<Employee> & { roleId?: string; role?: string }) => Promise<void>;
  /** Assign or clear an employee's reporting manager (org-chart edit). */
  setManager: (employeeId: string, managerId: string | null) => Promise<void>;
  deleteEmployee: (id: string) => Promise<void>;
  clearCurrentEmployee: () => void;
  clearError: () => void;
}

// Normalize DB snake_case rows → camelCase Employee shape
function normalizeEmployee(raw: any): any {
  if (!raw) return raw;
  const firstName = raw.first_name || raw.firstName || '';
  const lastName = raw.last_name || raw.lastName || '';
  const displayName =
    raw.display_name ||
    raw.displayName ||
    `${firstName} ${lastName}`.trim() ||
    raw.name ||
    raw.email ||
    'Employee';

  const rawAvatar =
    raw.avatar ||
    raw.avatar_url ||
    raw.avatarUrl ||
    raw.photo_url ||
    raw.photoUrl ||
    raw.picture ||
    raw.image;

  return {
    ...raw,
    id: raw.id,
    employeeCode: raw.employee_code || raw.employeeCode || '',
    firstName,
    lastName,
    displayName,
    avatar: rawAvatar || getUserAvatarUrl(displayName, raw.email),
    email: raw.email || '',
    phone: raw.phone || raw.phone_number || '',
    department: raw.department || '',
    designation: raw.designation || raw.title || '',
    role: raw.role_name || raw.user_role || raw.role || '',
    roleId: raw.role_id || raw.roleId || '',
    reportingManagerId: raw.manager_id || raw.reporting_manager_id || raw.reportingManagerId,
    status: raw.status || 'active',
    employmentType: raw.employment_type || raw.employmentType || 'full-time',
    joinDate: raw.join_date || raw.joinDate || '',
    location: raw.location || '',
    salary: raw.salary,
    currency: raw.currency || 'USD',
    skills: raw.skills || [],
    career: raw.career || [],
    documents: raw.documents || [],
  };
}

export const useEmployeeStore = create<EmployeeState>((set) => ({
  employees: [],
  currentEmployee: null,
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  stats: null,

  fetchEmployees: async (filters?: any) => {
    set({ isLoading: true, error: null });

    try {
      const response = await apiClient.get('/people/employees', { params: filters });
      const { data, pagination } = response.data;

      set({
        employees: (data || []).map(normalizeEmployee),
        pagination,
        isLoading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Failed to fetch employees',
        isLoading: false,
      });
    }
  },

  /**
   * Every employee matching `filters`, following pagination to the end.
   *
   * The list endpoint returns 20 rows by default and caps `limit` at 200, so a
   * single request only ever sees the first page. Export needs the whole set —
   * exporting "all employees" and silently getting the first 20 is worse than
   * failing, because the file looks complete.
   *
   * Returns the rows instead of writing them to `employees`, so exporting does
   * not disturb the table the user is looking at.
   */
  fetchAllEmployees: async (filters?: any) => {
    const LIMIT = 200; // MAX_LIMIT in the people service
    const all: Employee[] = [];
    let page = 1;

    // Bounded so a server that always reports another page cannot spin forever.
    for (let guard = 0; guard < 100; guard++) {
      const response = await apiClient.get('/people/employees', {
        params: { ...filters, page, limit: LIMIT },
      });
      const { data, pagination } = response.data;
      const batch = (data || []).map(normalizeEmployee);
      all.push(...batch);

      const totalPages = pagination?.totalPages ?? 1;
      if (batch.length === 0 || page >= totalPages) break;
      page += 1;
    }

    return all;
  },

  fetchStats: async () => {
    try {
      const response = await apiClient.get('/people/employees/stats');
      // Single-resource responses are wrapped as { data }.
      set({ stats: response.data?.data ?? response.data });
    } catch (error: any) {
      console.error('Failed to fetch employee stats', error);
    }
  },

  fetchEmployee: async (id: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await apiClient.get(`/people/employees/${id}`);
      set({
        currentEmployee: normalizeEmployee(response.data?.data ?? response.data),
        isLoading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Failed to fetch employee',
        isLoading: false,
      });
    }
  },

  createEmployee: async (data: Partial<Employee>) => {
    set({ isLoading: true, error: null });

    try {
      const response = await apiClient.post('/people/employees', data);
      const newEmployee = normalizeEmployee(response.data?.data ?? response.data);

      set((state) => ({
        employees: [newEmployee, ...state.employees],
        currentEmployee: newEmployee,
        isLoading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Failed to create employee',
        isLoading: false,
      });
      throw error;
    }
  },

  updateEmployee: async (id: string, data: Partial<Employee>) => {
    set({ isLoading: true, error: null });

    try {
      const response = await apiClient.patch(`/people/employees/${id}`, data);
      const updatedEmployee = response.data;

      set((state) => ({
        employees: state.employees.map((e) => (e.id === id ? updatedEmployee : e)),
        currentEmployee: updatedEmployee,
        isLoading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Failed to update employee',
        isLoading: false,
      });
      throw error;
    }
  },

  setManager: async (employeeId: string, managerId: string | null) => {
    set({ error: null });
    try {
      await apiClient.put(`/people/org/employees/${employeeId}/manager`, { managerId });
      // Reflect the change locally so the chart/table update without a full refetch.
      set((state) => ({
        employees: state.employees.map((e) =>
          e.id === employeeId ? { ...e, reportingManagerId: managerId || undefined } : e
        ),
      }));
    } catch (error: any) {
      set({ error: error.response?.data?.message || 'Failed to assign manager' });
      throw error;
    }
  },

  deleteEmployee: async (id: string) => {
    set({ isLoading: true, error: null });

    try {
      await apiClient.delete(`/people/employees/${id}`);

      set((state) => ({
        employees: state.employees.filter((e) => e.id !== id),
        currentEmployee: state.currentEmployee?.id === id ? null : state.currentEmployee,
        isLoading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Failed to delete employee',
        isLoading: false,
      });
      throw error;
    }
  },

  clearCurrentEmployee: () => {
    set({ currentEmployee: null });
  },

  clearError: () => {
    set({ error: null });
  },
}));
